import {
  Fragment,
  memo,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from "react";
import { useStore } from "react-redux";

import { useAuiState } from "../aui";
import type { AgentState } from "../store/agentSlice";
import { caretStyle, toCaretColour, type CaretColour } from "../theme";
import { clampOffset, clampRange, diffText, mapOffset } from "./text";
import { useLocalCaret } from "./useLocalCaret";
import { useMirrorBox } from "./useMirrorBox";
import { useTabCarets } from "./useTabCarets";

type State = { agent: AgentState };

interface Span {
  start: number;
  end: number;
  colour: CaretColour;
  className: string;
  key: string;
}

interface Point {
  at: number;
  colour: CaretColour;
  className: string;
  key: string;
}

interface Flash {
  start: number;
  end: number;
  colour: CaretColour;
  rev: number;
}

const CARET_HEAD =
  "after:absolute after:-left-[3.5px] after:-top-[3px] after:size-[7px] after:rounded-[2px_2px_2px_0] after:bg-caret";
const REMOTE_CARET = `relative before:absolute before:inset-y-0 before:-left-px before:w-0.5 before:rounded-[1px] before:bg-caret ${CARET_HEAD}`;
// This tab's own line is the native caret, tinted; the overlay adds only the head.
const LOCAL_CARET = `relative ${CARET_HEAD}`;
const SELECTION = "rounded-sm bg-caret/[.22]";
const INSERTED = "rounded-sm motion-safe:animate-caret-insert";
// Keeps a trailing newline's empty line, as the textarea shows it.
const LINE_END = String.fromCharCode(0x200b);
// Two layers in one grid cell wrap the text identically.
const LAYER = "[grid-area:1/1]";

/** The draft split at every marker: tinted ranges wrap the text, carets sit between it. */
function mirrorNodes(text: string, spans: Span[], points: Point[]) {
  const cuts = [
    ...new Set([
      0,
      text.length,
      ...spans.flatMap((s) => [s.start, s.end]),
      ...points.map((p) => p.at),
    ]),
  ].sort((a, b) => a - b);
  const nodes: ReactNode[] = [];
  cuts.forEach((at, index) => {
    for (const point of points.filter((p) => p.at === at)) {
      nodes.push(
        <span key={point.key} className={point.className} style={caretStyle(point.colour)} />
      );
    }
    const next = cuts[index + 1];
    if (next === undefined) return;
    let node: ReactNode = text.slice(at, next);
    for (const span of spans.filter((s) => s.start <= at && next <= s.end)) {
      node = (
        <span key={span.key} className={span.className} style={caretStyle(span.colour)}>
          {node}
        </span>
      );
    }
    nodes.push(<Fragment key={`text-${at}`}>{node}</Fragment>);
  });
  return nodes;
}

/**
 * The other tabs' carets and selections on this thread, drawn over the composer's textarea, plus
 * this tab's caret head and a flash on text another tab inserts. Mount it right after
 * ComposerPrimitive.Input, inside the same runtime, in a `relative` parent. A remote edit keeps this tab's caret on the
 * same words.
 */
export const CaretOverlay = memo(function CaretOverlay({
  input,
  threadId,
}: {
  input: RefObject<HTMLTextAreaElement | null>;
  threadId: number | null;
}) {
  const mirror = useRef<HTMLDivElement>(null);
  const store = useStore<State>();
  const text = useAuiState((state) => state.composer.text);
  const carets = useTabCarets(threadId);
  const { seen, local, reread } = useLocalCaret(input, threadId);
  const [flash, setFlash] = useState<Flash | null>(null);
  useMirrorBox(input, mirror);

  useLayoutEffect(() => {
    const element = input.current;
    if (!element) return;
    const before = seen.current;
    const after = element.value;
    if (mirror.current) mirror.current.scrollTop = element.scrollTop;
    if (!before) {
      seen.current = { text: after, start: element.selectionStart, end: element.selectionEnd };
      return;
    }
    if (after === before.text) {
      setFlash(null); // this tab typed
      return;
    }
    // Written into the textarea, not typed here: put the caret back on the same words. Writing the
    // value moved it to the end, focused or not; selectionchange reports it only while focused.
    const change = diffText(before.text, after);
    const start = mapOffset(before.start, change);
    const end = mapOffset(before.end, change);
    element.setSelectionRange(start, end);
    seen.current = { text: after, start, end };
    if (document.activeElement !== element) reread.current();

    const { agent } = store.getState();
    const remote = threadId == null ? undefined : agent.threads[threadId]?.composer.remote;
    const editor =
      remote?.text === after
        ? agent.presence.find((tab) => tab.tab_id === remote.tabId)
        : undefined;
    setFlash(
      remote && editor && change.insertedEnd > change.start
        ? {
            start: change.start,
            end: change.insertedEnd,
            colour: toCaretColour(editor.colour),
            rev: remote.rev,
          }
        : null
    );
  }, [input, reread, seen, store, text, threadId]);

  useEffect(() => {
    const element = input.current;
    if (!element || !carets.show) return;
    element.style.caretColor = `hsl(var(--caret-${carets.own}))`;
    return () => {
      element.style.caretColor = "";
    };
  }, [input, carets.show, carets.own]);

  let nodes: ReactNode[] = [];
  if (carets.show) {
    const spans: Span[] = [];
    const points: Point[] = [];
    for (const tab of carets.others) {
      const selection = clampRange(tab.selection, text.length);
      if (selection) {
        const [start, end] = selection;
        spans.push({
          start,
          end,
          colour: tab.colour,
          className: SELECTION,
          key: `sel-${tab.tabId}`,
        });
      }
      const at = clampOffset(tab.caret, text.length);
      if (at != null) {
        points.push({ at, colour: tab.colour, className: REMOTE_CARET, key: `caret-${tab.tabId}` });
      }
    }
    // Only beside another tab's caret (the points so far): alone (the other tab idle, or not in
    // its composer), this tab's head would be a marker that stands for no one.
    if (local.focused && points.length > 0) {
      points.push({
        at: Math.min(local.caret, text.length),
        colour: carets.own,
        className: LOCAL_CARET,
        key: "caret-local",
      });
    }
    nodes = mirrorNodes(text, spans, points);
  }

  // Its own layer, so carets moving during the 1.6 s never re-slice (and replay) it.
  const inserted = carets.show && flash && flash.end <= text.length && (
    <div className={LAYER}>
      {text.slice(0, flash.start)}
      <span
        key={flash.rev}
        className={INSERTED}
        style={caretStyle(flash.colour)}
        onAnimationEnd={() => setFlash(null)}
      >
        {text.slice(flash.start, flash.end)}
      </span>
      {text.slice(flash.end)}
      {LINE_END}
    </div>
  );

  return (
    <div
      ref={mirror}
      aria-hidden
      className="pointer-events-none absolute grid select-none overflow-hidden text-transparent"
    >
      {inserted}
      <div className={LAYER}>
        {nodes}
        {carets.show && LINE_END}
      </div>
    </div>
  );
});
